import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { getDisplayUsername } from "~/utils";

export type AuthStatus = "unknown" | "authorized" | "expired";

interface AuthContextType {
  status: AuthStatus;
  setIsAuthorized: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({
  status: "unknown",
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsAuthorized: (_value: boolean) => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() =>
    getDisplayUsername() ? "authorized" : "unknown",
  );

  useEffect(() => {
    // Only promotes out of "unknown", so it never overrides a status an
    // explicit 401 elsewhere already set.
    const upgrade = (next: AuthStatus) =>
      setStatus((current) => (current === "unknown" ? next : current));
    fetch("/api/session")
      .then((response) => {
        if (response.status === 401) setStatus("expired");
        else if (response.ok) upgrade("authorized");
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
  }, []);

  const setIsAuthorized = useCallback((value: boolean) => {
    setStatus((current) =>
      value ? (current === "expired" ? current : "authorized") : "expired",
    );
  }, []);

  return (
    <AuthContext.Provider value={{ status, setIsAuthorized }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
